import React, { useEffect, useState, useRef } from "react";
import Navbar from "../components/Navbar";
import { useNavigate } from "react-router-dom";
import { Paths } from "../router/Paths";
import { usePackage } from "../hooks/usePackage";
import { toast } from "react-toastify";
import RoleBasedComponent from "../components/RoleBasedComponent";

// TODO: fix the mobile view

export default function ManageProductPage() {
  const navigate = useNavigate();
  const { getPackage, approvePackage, rejectPackage, deletePackage } =
    usePackage();
  const [packages, setPackages] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedPackageId, setSelectedPackageId] = useState<string | null>(
    null
  );
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [showModal, setShowModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");

  const getPackages = async () => {
    try {
      setLoading(true);
      const response = await getPackage({ setLoading });
      if (response.status === 200) {
        setPackages(response.packages || []);
      }
    } catch (error) {
      console.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getPackages();
  }, []);

  const handleCreateProduct = () => {
    navigate(Paths.tourManagementCreate);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleStatusFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleActionSelect = (action: string, id: string) => {
    if (action === "") {
      // Toggle dropdown
      setSelectedPackageId(selectedPackageId === id ? null : id);
      setSelectedAction(null);
    } else if (action === "view") {
      // Use Paths.manageProductDetails and replace :id with actual id
      navigate(Paths.manageProductDetails.replace(":id", id));
      // Do NOT show modal for view
    } else {
      // Select an action and show modal
      setSelectedAction(action);
      setSelectedPackageId(id);
      setShowModal(true);
      if (action !== "reject") setRejectionReason("");
    }
  };

  const handleActionConfirm = async () => {
    if (!selectedAction || !selectedPackageId) return;

    try {
      switch (selectedAction) {
        case "approve":
          await approvePackage(selectedPackageId);
          toast.success("Package approved successfully");
          break;
        case "reject":
          await rejectPackage(selectedPackageId, {
            reasonForRejection: rejectionReason,
          });
          toast.success("Package rejected successfully");
          break;
        case "delete":
          await deletePackage(selectedPackageId);
          toast.success("Package deleted successfully");
          break;
      }
      // Refresh the packages list
      getPackages();
      // Reset selection
      setSelectedAction(null);
      setSelectedPackageId(null);
      setShowModal(false);
      setRejectionReason("");
    } catch (error) {
      toast.error("Failed to perform action");
      console.error("Error:", error);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedAction(null);
    setSelectedPackageId(null);
    setRejectionReason("");
  };

  const filteredPackages = packages
    .filter((pkg) => {
      const matchesSearch = pkg.title
        ?.toLowerCase()
        .includes(searchQuery.toLowerCase());
      const matchesStatus =
        statusFilter === "all" ||
        pkg.status?.toLowerCase() === statusFilter.toLowerCase();

      return matchesSearch && matchesStatus;
    })
    .sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    ); // Sort by createdAt (new to old)

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredPackages.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  return (
    <div>
      <Navbar />
      <div className="max-w-7xl mx-auto mt-10">
        <div className="p-4 space-y-4">
          <div className="bg-yellow-50 border border-yellow-300 text-yellow-800 p-4 rounded text-sm mb-5">
            <p>
              We are no longer accepting new listings for the following product
              types: eSIMs, WiFi devices, self-guided audio tours, scavenger
              hunts, and vehicle rentals. Please note that existing products of
              these types may be removed from our platform if identified.
            </p>
          </div>
          {/* Filters and Actions */}
          <div className="flex flex-wrap gap-4 items-center justify-between my-5">
            {/* Filter Inputs */}
            <div className="flex space-x-4">
              {/* Filter by Product */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">
                  Filter by product
                </label>
                <div className="flex items-center border border-gray-300 rounded px-2">
                  <svg
                    className="w-4 h-4 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 21l-4.35-4.35m-3.9 0A7.5 7.5 0 1117.1 4.9a7.5 7.5 0 010 10.6z"
                    />
                  </svg>
                  <input
                    type="text"
                    placeholder="Search"
                    className="pl-2 py-1 w-full text-sm outline-none"
                    value={searchQuery}
                    onChange={handleSearch}
                  />
                </div>
              </div>

              {/* Filter by Status */}
              <div>
                <label className="block text-sm text-gray-500 mb-1">
                  Filter by status
                </label>
                <select
                  className="border border-gray-300 rounded px-2 py-1 text-sm w-full"
                  value={statusFilter}
                  onChange={handleStatusFilter}
                >
                  <option value="all">All</option>
                  <option value="pending">Pending</option>
                  <option value="rejected">Rejected</option>
                  <option value="approved">Approved</option>
                </select>
              </div>
            </div>

            {/* Showing Info and Create Button */}
            <div className="flex items-center space-x-4">
              {/* Showing Info */}
              <p className="text-sm text-gray-500">
                Showing {indexOfFirstItem + 1} -{" "}
                {Math.min(indexOfLastItem, filteredPackages.length)} out of{" "}
                {filteredPackages.length}
              </p>

              {/* Create New Product */}
              <button
                onClick={handleCreateProduct}
                className="text-blue-600 text-sm font-medium flex items-center hover:underline"
              >
                <svg
                  className="w-4 h-4 mr-1"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Create new product
              </button>
            </div>
          </div>

          {/* Information Banner */}

          {/* Table Header */}
          <div className="grid grid-cols-4 text-sm font-bold text-gray-600 border-b pb-2 ml-10 mt-5">
            <div>Product</div>
            <div>Reference code</div>
            <div>Status</div>
            <div>Action</div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          )}

          {/* No Content State */}
          {!loading && currentItems.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No packages found</p>
              <p className="text-sm text-gray-400 mt-2">
                {searchQuery || statusFilter !== "all"
                  ? "Try adjusting your filters"
                  : "Create your first package to get started"}
              </p>
            </div>
          )}

          {/* Product Rows */}
          {!loading &&
            currentItems.length > 0 &&
            currentItems.map((pkg, idx) => (
              <div
                key={idx}
                className="grid grid-cols-2 lg:grid-cols-4 items-center gap-4 bg-white p-4 border border-gray-300 rounded-md shadow-sm"
              >
                {/* Product Section */}
                <div className="flex items-center">
                  <div className="w-28 h-24 bg-gray-300 rounded-md mr-4 flex-shrink-0">
                    {pkg.gallery && pkg.gallery.length > 0 && (
                      <img
                        src={pkg.gallery[0]}
                        alt="Product"
                        className="w-full h-full object-cover rounded-md"
                      />
                    )}
                  </div>
                  <div>
                    <p className="font-semibold text-gray-800">{pkg.title}</p>
                    <p className="text-sm text-gray-500">No ratings</p>
                  </div>
                </div>

                {/* Reference Code */}
                <div className="text-gray-800 text-sm">{pkg.referenceCode}</div>

                {/* Status */}
                <div>
                  {pkg.status === "pending" ? (
                    <span className="text-yellow-600 bg-yellow-100 px-3 py-1 rounded-full text-sm">
                      Pending
                    </span>
                  ) : pkg.status === "rejected" ? (
                    <span className="text-red-600 bg-red-100 px-3 py-1 rounded-full text-sm">
                      Rejected
                    </span>
                  ) : pkg.status === "approved" ? (
                    <span className="text-green-600 bg-green-100 px-3 py-1 rounded-full text-sm">
                      Approved
                    </span>
                  ) : (
                    <span className="text-gray-600 bg-gray-100 px-3 py-1 rounded-full text-sm">
                      {pkg.status}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="text-right relative" ref={dropdownRef}>
                  <button
                    onClick={() => handleActionSelect("", pkg._id)}
                    className="text-white bg-blue-600 px-4 py-2 rounded text-sm hover:bg-blue-700"
                  >
                    Actions
                  </button>
                  {selectedPackageId === pkg._id && !showModal && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10">
                      <div className="py-1">
                        <button
                          onClick={() => handleActionSelect("view", pkg._id)}
                          className="block w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-gray-100"
                        >
                          View
                        </button>
                        <RoleBasedComponent allowedRoles={["admin"]}>
                          <button
                            onClick={() =>
                              handleActionSelect("approve", pkg._id)
                            }
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Approve
                          </button>
                        </RoleBasedComponent>
                        <RoleBasedComponent allowedRoles={["admin"]}>
                          <button
                            onClick={() =>
                              handleActionSelect("reject", pkg._id)
                            }
                            className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                          >
                            Reject
                          </button>
                        </RoleBasedComponent>
                        <button
                          onClick={() => handleActionSelect("delete", pkg._id)}
                          className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}

          {/* Pagination */}
          <div className="flex items-center justify-between text-sm mt-4">
            <div>
              <span className="mr-2">{itemsPerPage}</span>
              <span>Per page</span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                className="text-gray-500 px-2 py-1 border rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                Previous
              </button>
              <button
                className="text-gray-500 px-2 py-1 border rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={indexOfLastItem >= filteredPackages.length}
              >
                Next
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal for Confirming Actions */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
          <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-semibold mb-4">
              {selectedAction === "approve" && "Approve Product"}
              {selectedAction === "reject" && "Reject Product"}
              {selectedAction === "delete" && "Delete Product"}
            </h2>
            <p className="mb-4">
              {selectedAction === "approve" &&
                "Are you sure you want to approve this product?"}
              {selectedAction === "reject" &&
                "Are you sure you want to reject this product?"}
              {selectedAction === "delete" &&
                "Are you sure you want to delete this product?"}
            </p>
            {selectedAction === "reject" && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">
                  Reason for rejection
                </label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason"
                />
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <button
                onClick={handleModalClose}
                className="px-4 py-2 rounded text-sm bg-gray-200 text-gray-700 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={handleActionConfirm}
                className={`px-4 py-2 rounded text-sm text-white ${
                  selectedAction === "delete"
                    ? "bg-red-600 hover:bg-red-700"
                    : selectedAction === "approve"
                    ? "bg-green-600 hover:bg-green-700"
                    : "bg-yellow-600 hover:bg-yellow-700"
                }`}
                disabled={
                  selectedAction === "reject" && !rejectionReason.trim()
                }
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
